export type LabelFileFormat = 'csv' | 'tsv'

export interface LabelRow {
  run_id: string
  cell_id: string
  label: string
  labeler_id: string
  labeled_at: string
}

const COLUMNS: (keyof LabelRow)[] = ['run_id', 'cell_id', 'label', 'labeler_id', 'labeled_at']

/** Quote a CSV field if it contains a comma, quote, or newline. */
const escapeCsv = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

/** Tabs and newlines would corrupt a TSV row, so collapse them to spaces. */
const escapeTsv = (value: string): string => value.replace(/[\t\n\r]+/g, ' ')

/** Generates the label file contents for the given rows and delimiter format. */
export const generateLabelFile = (rows: LabelRow[], format: LabelFileFormat): string => {
  const delimiter = format === 'csv' ? ',' : '\t'
  const escape = format === 'csv' ? escapeCsv : escapeTsv
  const header = COLUMNS.join(delimiter)
  const lines = rows.map((row) =>
    COLUMNS.map((column) => escape(String(row[column] ?? ''))).join(delimiter)
  )
  return [header, ...lines].join('\n')
}

/** Splits one delimited line, honoring quoted fields for CSV. */
const splitLine = (line: string, delimiter: string): string[] => {
  if (delimiter === '\t') return line.split('\t')

  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"'
        i += 1
      } else if (char === '"') {
        inQuotes = false
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

/**
 * Parses a CSV/TSV label file. Delimiter is inferred from the header. Requires
 * at least cell_id and label columns. Throws a descriptive error if malformed.
 */
export const parseLabelFile = (text: string): LabelRow[] => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  if (lines.length === 0) throw new Error('The file is empty.')

  const delimiter = lines[0].includes('\t') ? '\t' : ','
  const header = splitLine(lines[0], delimiter).map((column) => column.trim().toLowerCase())
  const cellIdIndex = header.indexOf('cell_id')
  const labelIndex = header.indexOf('label')
  if (cellIdIndex === -1 || labelIndex === -1) {
    throw new Error('File is missing the required cell_id and label columns.')
  }

  const runIdIndex = header.indexOf('run_id')
  const labelerIndex = header.indexOf('labeler_id')
  const labeledAtIndex = header.indexOf('labeled_at')

  const rows: LabelRow[] = []
  for (let i = 1; i < lines.length; i += 1) {
    const columns = splitLine(lines[i], delimiter)
    const cellId = (columns[cellIdIndex] ?? '').trim()
    const label = (columns[labelIndex] ?? '').trim()
    if (cellId && label) {
      rows.push({
        run_id: runIdIndex >= 0 ? (columns[runIdIndex] ?? '').trim() : '',
        cell_id: cellId,
        label,
        labeler_id: labelerIndex >= 0 ? (columns[labelerIndex] ?? '').trim() : '',
        labeled_at: labeledAtIndex >= 0 ? (columns[labeledAtIndex] ?? '').trim() : '',
      })
    }
  }

  if (rows.length === 0) throw new Error('No labeled rows found in the file.')
  return rows
}

/** File name like labels_<runId>_<YYYY-MM-DD>.csv */
export const buildLabelFileName = (
  runId: string,
  format: LabelFileFormat,
  isoDate: string
): string => `labels_${runId}_${isoDate}.${format}`

/** Triggers a browser download of a text file. */
export const downloadTextFile = (
  fileName: string,
  contents: string,
  mimeType = 'text/plain;charset=utf-8'
): void => {
  const blob = new Blob([contents], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
